import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { AgendaService } from '../../../services/business/agenda.service';
import { AgendaEvent } from '../../../shared/interfaces';
import { ToastService, PageTitleService } from '../../../shared/services';
import { AuthService } from '../../../services/auth';

@Component({
  selector: 'app-booking-requests',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './booking-requests.html',
  styleUrl: './booking-requests.css',
})
export class BookingRequests implements OnInit, OnDestroy {
  private agendaService = inject(AgendaService);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private titleService = inject(PageTitleService);

  events = signal<AgendaEvent[]>([]);
  isLoading = signal(false);
  selectedOrigin = signal<'all' | 'Website' | 'AiAgent'>('all');
  selectedDateRange = signal<{ start?: Date; end?: Date }>({});

  filteredEvents = computed(() => {
    const events = this.events();
    const origin = this.selectedOrigin();

    if (origin === 'all') {
      return events;
    }

    return events.filter(e => e.origin === origin);
  });

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Solicitudes de cita',
      subtitle: 'Citas que tus clientes pidieron desde tu enlace de reservas',
      breadcrumbs: [{ name: 'Calendario', url: '/agendas' }],
    });
    this.loadEvents();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  loadEvents(): void {
    const businessId = this.authService.getUserInfo()?.business_id;
    if (!businessId) {
      this.toastService.show('No se pudo identificar el negocio.', 'warning');
      return;
    }

    this.isLoading.set(true);

    const params: any = { businessId };
    const dateRange = this.selectedDateRange();
    if (dateRange.start) {
      params.startDate = dateRange.start;
    }
    if (dateRange.end) {
      params.endDate = dateRange.end;
    }
    if (this.selectedOrigin() !== 'all') {
      params.origin = this.selectedOrigin();
    }

    this.agendaService.getExternalEvents(params).subscribe({
      next: (response) => {
        this.events.set(response);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toastService.show(
          'No pudimos cargar las reservas externas. Intenta nuevamente.',
          'warning'
        );
      },
    });
  }

  filterByOrigin(origin: 'all' | 'Website' | 'AiAgent'): void {
    this.selectedOrigin.set(origin);
    this.loadEvents();
  }

  onDateRangeChange(start: Date | null, end: Date | null): void {
    this.selectedDateRange.set({
      start: start || undefined,
      end: end || undefined,
    });
    this.loadEvents();
  }

  getOriginLabel(origin?: string): string {
    const originMap: Record<string, string> = {
      Website: 'Sitio web',
      AiAgent: 'Asistente IA',
      Calendar: 'Calendario',
    };
    return originMap[origin || ''] || origin || 'Desconocido';
  }

  getOriginBadgeClass(origin?: string): string {
    const classMap: Record<string, string> = {
      Website: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      AiAgent: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      Calendar: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    };
    return classMap[origin || ''] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }

  getStatusLabel(statusCode: string): string {
    const statusMap: Record<string, string> = {
      scheduled: 'Programada',
      confirmed: 'Confirmada',
      completed: 'Completada',
      cancelled: 'Cancelada',
      hidden: 'Oculta',
    };
    return statusMap[statusCode] || statusCode;
  }

  getStatusBadgeClass(statusCode: string): string {
    const classMap: Record<string, string> = {
      scheduled: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      confirmed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      hidden: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    };
    return classMap[statusCode] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
}
